/**
 * Formations (Phase 66.3, decision 119; lane D's design note in BUILDLIST Phase 66). A formation is part
 * of a squad's **base order**: a shape, a spacing and who leads. The leader carries out the squad's order
 * as any bot does; every other member keeps a **slot** — a place relative to the leader and the squad's
 * heading — whenever it has nothing more pressing (no enemy in sight). Shapes:
 *   - column: one behind another along the heading (a march);
 *   - wedge:  the leader at the point, the rest fanning back left and right (a move to contact);
 *   - line:   abreast across the heading (an assault or a sweep).
 * Slots are offsets in person lengths (forward, right); on the planet they are walked along great
 * circles from the leader. Pure; deterministic math only.
 */
import type { GameState, MatchSquad, PlayerState, Vec3 } from '../types';
import { vecAdd, vecCross, vecLength, vecNormalize, vecScale } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { headingOf, projectToTangent } from './surfaceWalk';

export type FormationShape = 'column' | 'wedge' | 'line';
export const FORMATION_SHAPES: FormationShape[] = ['column', 'wedge', 'line'];

export interface Formation {
  shape: FormationShape;
  /** Between neighbours, in person lengths (the scale profile's unit radius × 2 is one person). */
  spacing: number;
  /** The unit that leads; absent = the senior living member (the lowest id). */
  leader?: number;
}

export const DEFAULT_FORMATION_SPACING = 2.5;

/** Slot offsets (forward, right) in person lengths for `n` members; slot 0 is the leader's. */
export function formationSlots(shape: FormationShape, n: number, spacing = DEFAULT_FORMATION_SPACING): Array<{ forward: number; right: number }> {
  const out: Array<{ forward: number; right: number }> = [];
  for (let i = 0; i < n; i++) {
    if (i === 0) { out.push({ forward: 0, right: 0 }); continue; }
    if (shape === 'column') out.push({ forward: -i * spacing, right: 0 });
    else if (shape === 'wedge') {
      const k = Math.ceil(i / 2), side = i % 2 === 1 ? 1 : -1;
      out.push({ forward: -k * spacing * 0.8, right: side * k * spacing });
    } else {
      // a line: the leader at the centre, members alternating right and left
      const k = Math.ceil(i / 2), side = i % 2 === 1 ? 1 : -1;
      out.push({ forward: 0, right: side * k * spacing });
    }
  }
  return out;
}

/** The living members of a squad in slot order: the slot leader first, then by id (62.43: a seated slot leader yields to the first member on foot). */
export function formationOrder(state: Pick<GameState, 'players'>, squad: Pick<MatchSquad, 'unitIds' | 'formation'>): PlayerState[] {
  const alive = squad.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p && p.alive).sort((a, b) => a.id - b.id);
  const lead = squad.formation?.leader;
  const i = lead === undefined ? -1 : alive.findIndex(p => p.id === lead);
  if (i > 0) { const [l] = alive.splice(i, 1); alive.unshift(l); }
  // 62.43: a member crewing a seat (a gun) cannot be the slot leader the walkers form on — the members on foot first, in the
  // same order, the seated after; a squad all on foot is as it was. (The formation's slot leader, not 68.9's command
  // leader — decision 139 makes that a designated unit with no promotion.)
  if (alive[0]?.mount) return [...alive.filter(p => !p.mount), ...alive.filter(p => p.mount)];
  return alive;
}

/** The formation's slot leader right now (the named one while alive and on foot, else the senior living member on foot, else the senior seated), or null when nobody lives. Not 68.9's command leader (decision 139). */
export function formationLeader(state: Pick<GameState, 'players'>, squad: Pick<MatchSquad, 'unitIds' | 'formation'>): PlayerState | null {
  return formationOrder(state, squad)[0] ?? null;
}

/**
 * Where a member should stand: its slot walked from the leader along the leader's heading (forward) and
 * across it (right), as a surface direction on the leader's planet. `personLength` is in world units.
 * Null for the leader, a member of no formation, a dead leader or one on another planet.
 */
export function formationSlotDir(state: Pick<GameState, 'players' | 'planets'>, squad: MatchSquad, unitId: number, personLength: number): Vec3 | null {
  if (!squad.formation) return null;
  const order = formationOrder(state, squad);
  const idx = order.findIndex(p => p.id === unitId);
  if (idx <= 0) return null;
  const leader = order[0];
  const me = order[idx];
  if (me.planetIndex !== leader.planetIndex) return null;
  const planet = state.planets[leader.planetIndex];
  if (!planet) return null;
  const slot = formationSlots(squad.formation.shape, order.length, squad.formation.spacing)[idx];
  const n = vecNormalize(leader.surfaceDir);
  const fwd = headingOf(leader);
  const right = vecNormalize(vecCross(fwd, n));
  // the offset in the leader's ground plane, then walked along the great circle in that direction
  const off = vecAdd(vecScale(fwd, slot.forward * personLength), vecScale(right, slot.right * personLength));
  const dist = vecLength(off);
  if (dist < 1e-9) return n;
  const t = projectToTangent(off, n);
  const theta = dist / planet.radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(theta)), vecScale(t, dsin(theta))));
}

/** How far the squad's worst straggler is from its slot (world units); 0 with nobody behind. The slot leader waits on it (62.43: a seated member is at its job, never a straggler). */
export function formationLag(state: Pick<GameState, 'players' | 'planets'>, squad: MatchSquad, personLength: number): number {
  let worst = 0;
  for (const p of formationOrder(state, squad).slice(1)) {
    if (p.mount) continue;   // 62.43: a member crewing a seat is at its job, not lagging — the squad does not wait for it
    const slot = formationSlotDir(state, squad, p.id, personLength);
    if (!slot) continue;
    worst = Math.max(worst, slotError(p.surfaceDir, slot, state.planets[p.planetIndex].radius));
  }
  return worst;
}

/** The arc (world units) between two surface directions on a planet of `radius`. */
export function slotError(a: Vec3, b: Vec3, radius: number): number {
  const d = Math.max(-1, Math.min(1, vecNormalize(a).x * vecNormalize(b).x + vecNormalize(a).y * vecNormalize(b).y + vecNormalize(a).z * vecNormalize(b).z));
  return dacos(d) * radius;
}

/** Mean distance of every non-leader member from its slot, per squad with a formation (world units); null without any. */
export function formationError(state: Pick<GameState, 'players' | 'planets' | 'squads'>, personLength: number): number | null {
  let sum = 0, n = 0;
  for (const sq of state.squads ?? []) {
    if (!sq.formation) continue;
    for (const p of formationOrder(state, sq).slice(1)) {
      const slot = formationSlotDir(state, sq, p.id, personLength);
      if (!slot) continue;
      sum += slotError(p.surfaceDir, slot, state.planets[p.planetIndex].radius);
      n++;
    }
  }
  return n ? sum / n : null;
}
